// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Grid, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import type { Condition, Encounter, MedicationStatement, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { showErrorNotification } from '../../utils/notifications';

const POSITIONAL_APNEA_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/suspected-positional-apnea';
const CONDITION_CATEGORY_SYSTEM = 'https://polarissleep.com/fhir/condition-category';

interface ClaimMedicalInfoSectionProps {
  patient: Patient;
  encounter: Encounter | undefined;
}

function getConditionLabel(c: Condition): string {
  const code = c.code?.coding?.[0]?.code;
  const display = c.code?.text ?? c.code?.coding?.[0]?.display ?? '—';
  return code ? `${display} (${code})` : display;
}

function hasConditionCategory(c: Condition, categoryCode: string): boolean {
  return (
    c.category?.some((cat) =>
      cat.coding?.some((cod) => cod.system === CONDITION_CATEGORY_SYSTEM && cod.code === categoryCode)
    ) ?? false
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string | undefined }): JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value ?? '—'}</Text>
    </Stack>
  );
}

export function ClaimMedicalInfoSection({ patient, encounter }: ClaimMedicalInfoSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [medications, setMedications] = useState<MedicationStatement[]>([]);
  const [medsLoading, setMedsLoading] = useState(true);

  useEffect(() => {
    if (!patient.id) {
      return;
    }
    medplum
      .searchResources('Condition', { subject: `Patient/${patient.id}` })
      .then(setConditions)
      .catch(showErrorNotification)
      .finally(() => setLoading(false));
  }, [medplum, patient.id]);

  useEffect(() => {
    if (!patient.id) {
      return;
    }
    medplum
      .searchResources('MedicationStatement', { subject: `Patient/${patient.id}`, status: 'active' })
      .then(setMedications)
      .catch(showErrorNotification)
      .finally(() => setMedsLoading(false));
  }, [medplum, patient.id]);

  const positionalApnea = encounter?.extension?.find((e) => e.url === POSITIONAL_APNEA_EXTENSION)?.valueString;

  const encounterDiagnoses = encounter?.diagnosis ?? [];
  const diagnosisConditions = encounterDiagnoses
    .map((diagnosis) => {
      const id = diagnosis.condition?.reference?.split('/').pop();
      return conditions.find((condition) => condition.id === id);
    })
    .filter((condition): condition is Condition => Boolean(condition));
  const primary = diagnosisConditions[0];
  const additional = diagnosisConditions.slice(1);

  const symptomConditions = conditions.filter((c) => hasConditionCategory(c, 'symptom'));
  const comorbidityConditions = conditions.filter((c) => hasConditionCategory(c, 'comorbidity'));

  if (loading) {
    return (
      <Paper withBorder p="md">
        <Title order={3} mb="md">
          Diagnoses
        </Title>
        <Loader size="sm" />
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md">
      <Title order={3} mb="md">
        Medical Info
      </Title>
      <Stack gap="md">
        {/* Diagnoses */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ReadOnlyField label="Primary Diagnosis" value={primary ? getConditionLabel(primary) : undefined} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ReadOnlyField
              label="Additional Diagnoses"
              value={additional.length > 0 ? additional.map(getConditionLabel).join(', ') : 'None'}
            />
          </Grid.Col>
        </Grid>

        {/* Positional apnea */}
        <ReadOnlyField label="Suspected Positional Apnea?" value={positionalApnea} />

        {/* Current Medications */}
        <Stack gap="xs">
          <Text fw={600} size="md">
            Current Medications
          </Text>
          {medsLoading && <Loader size="xs" />}
          {!medsLoading && medications.length === 0 && (
            <Text size="sm" c="dimmed">
              None
            </Text>
          )}
          {!medsLoading && medications.length > 0 && (
            <Stack gap="xs">
              {medications.map((med) => {
                const dq = med.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity;
                const freq = med.dosage?.[0]?.timing?.code?.coding?.[0]?.code;
                const name =
                  med.medicationCodeableConcept?.text ?? med.medicationCodeableConcept?.coding?.[0]?.display ?? '—';
                const dosageParts = [
                  dq?.value !== undefined ? String(dq.value) : undefined,
                  dq?.unit ?? dq?.code,
                  freq,
                ].filter(Boolean);
                return (
                  <Grid key={med.id} align="center">
                    <Grid.Col span={{ base: 12, md: 5 }}>
                      <ReadOnlyField label="Medication" value={name} />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 7 }}>
                      <ReadOnlyField
                        label="Dosage / Frequency"
                        value={dosageParts.length > 0 ? dosageParts.join(' ') : undefined}
                      />
                    </Grid.Col>
                  </Grid>
                );
              })}
            </Stack>
          )}
        </Stack>

        {/* Symptoms */}

        <Stack gap="xs">
          <Text fw={600} size="md">
            Symptoms
          </Text>
          {symptomConditions.length === 0 ? (
            <Text size="sm" c="dimmed">
              None
            </Text>
          ) : (
            <Stack gap="xs">
              {symptomConditions.map((c) => (
                <Grid key={c.id} align="center">
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <ReadOnlyField label="Symptom" value={c.code?.text ?? c.code?.coding?.[0]?.display} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 4, md: 2 }}>
                    <ReadOnlyField label="Severity" value={c.severity?.text} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 4, md: 2 }}>
                    <ReadOnlyField label="Frequency" value={c.note?.[0]?.text} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 4, md: 3 }}>
                    <ReadOnlyField label="Onset" value={c.onsetDateTime ? c.onsetDateTime.split('T')[0] : undefined} />
                  </Grid.Col>
                </Grid>
              ))}
            </Stack>
          )}
        </Stack>

        {/* Comorbidities */}
        <Stack gap="xs">
          <Text fw={600} size="md">
            Comorbidities
          </Text>
          {comorbidityConditions.length === 0 ? (
            <Text size="sm" c="dimmed">
              None
            </Text>
          ) : (
            <Stack gap="xs">
              {comorbidityConditions.map((c) => (
                <Grid key={c.id} align="center">
                  <Grid.Col span={{ base: 12, md: 5 }}>
                    <ReadOnlyField label="Condition" value={c.code?.text ?? c.code?.coding?.[0]?.display} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, md: 3 }}>
                    <ReadOnlyField label="Onset" value={c.onsetDateTime ? c.onsetDateTime.split('T')[0] : undefined} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, md: 3 }}>
                    <ReadOnlyField label="ICD-10 Code" value={c.code?.coding?.[0]?.code} />
                  </Grid.Col>
                </Grid>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
